import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, remove } from 'firebase/database';
import { auth } from '../firebase';
import SalesReturnForm from './SalesReturnForm';

const SalesReturnTable = () => {
  const [salesReturns, setSalesReturns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Fetch current user
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Fetch customers data
  useEffect(() => {
    if (!currentUser) return;

    const fetchCustomers = async () => {
      try {
        const encodedEmail = encodeEmailForPath(currentUser.email);
        const customersRef = ref(getDatabase(), `users/${encodedEmail}/customers`);
        const snapshot = await get(customersRef);

        if (snapshot.exists()) {
          const customersData = snapshot.val();
          const customersArray = Object.entries(customersData).map(([key, value]) => ({
            id: key,
            ...value,
          }));
          setCustomers(customersArray);
        } else {
          setCustomers([]);
        }
      } catch (err) {
        console.error('Error fetching customers:', err);
        setError('Failed to load customers data');
      }
    };

    fetchCustomers();
  }, [currentUser]);

  // Fetch sales return data
  useEffect(() => {
    if (!currentUser) return;

    const fetchSalesReturns = async () => {
      setLoading(true);
      try {
        const encodedEmail = encodeEmailForPath(currentUser.email);
        const salesReturnsRef = ref(getDatabase(), `users/${encodedEmail}/salesReturns`);
        const snapshot = await get(salesReturnsRef);

        if (snapshot.exists()) {
          const returnsData = snapshot.val();
          const returnsArray = Object.entries(returnsData).map(([key, value]) => ({
            id: key,
            ...value,
          }));
          setSalesReturns(returnsArray);
        } else {
          setSalesReturns([]);
        }
      } catch (err) {
        console.error('Error fetching sales returns:', err);
        setError('Failed to load sales returns');
      } finally {
        setLoading(false);
      }
    };

    fetchSalesReturns();
  }, [currentUser]);

  // Helper function to get customer mobile number by customer name
  const getCustomerMobileNo = (customerName) => {
    const customer = customers.find(c => c.customerName === customerName);
    return customer?.mobileNo || 'N/A';
  };

  // Helper function to get customer details by customer name
  const getCustomerDetails = (customerName) => {
    return customers.find(c => c.customerName === customerName) || {};
  };

  // Handle delete action
  const handleDelete = async (id) => {
    if (!currentUser) {
      setError('Please log in to delete sales returns.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this sales return?')) return;

    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const returnRef = ref(getDatabase(), `users/${encodedEmail}/salesReturns/${id}`);
      await remove(returnRef);
      setSalesReturns((prev) => prev.filter((item) => item.id !== id));
      setError('');
    } catch (err) {
      console.error('Error deleting sales return:', err);
      setError('Failed to delete sales return');
    }
  };

  // Handle edit action
  const handleEdit = (item) => {
    setEditData(item);
    setShowForm(true);
  };

  // Handle form close and refresh data
  const handleFormClose = () => {
    setShowForm(false);
    setEditData(null);
    if (currentUser) {
      const fetchSalesReturns = async () => {
        try {
          const encodedEmail = encodeEmailForPath(currentUser.email);
          const salesReturnsRef = ref(getDatabase(), `users/${encodedEmail}/salesReturns`);
          const snapshot = await get(salesReturnsRef);

          if (snapshot.exists()) {
            const returnsData = snapshot.val();
            const returnsArray = Object.entries(returnsData).map(([key, value]) => ({
              id: key,
              ...value,
            }));
            setSalesReturns(returnsArray);
          } else {
            setSalesReturns([]);
          }
        } catch (err) {
          console.error('Error fetching sales returns:', err);
          setError('Failed to load sales returns');
        }
      };
      fetchSalesReturns();
    }
  };

  // Styles aligned with original design
  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '2rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontWeight: '600',
      color: '#374151',
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2563eb',
      margin: '0',
    },
    addButton: {
      padding: '0.875rem 2rem',
      borderRadius: '8px',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      transform: 'translateY(0)',
    },
    tableContainer: {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
    },
    tableHeader: {
      padding: '1rem 0.75rem',
      textAlign: 'center',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
    },
    tableCell: {
      padding: '1rem 0.75rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'center',
    },
    actionCell: {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0.5rem',
    },
    editButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    deleteButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    errorMessage: {
      padding: '1rem 1.5rem',
      marginBottom: '1.5rem',
      borderRadius: '12px',
      backgroundColor: '#fee2e2',
      color: '#b91c1c',
      border: '1px solid #fecaca',
      fontSize: '0.9rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    loading: {
      textAlign: 'center',
      padding: '4rem',
      color: '#718096',
      fontSize: '1.1rem',
    },
    noData: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#718096',
      fontSize: '1.1rem',
    },
   
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#718096', fontSize: '1.1rem' }}>
          <h2>Please login to view sales returns</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {showForm ? (
        <SalesReturnForm
          userEmail={currentUser.email}
          onClose={handleFormClose}
          initialData={editData}
        />
      ) : (
        <>
          <div style={styles.header}>
            <h1 style={styles.title}>Sales Returns</h1>
            <button
              style={styles.addButton}
              onClick={() => {
                setShowForm(true);
                setEditData(null);
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
              }}
            >
              + Add Sales Return
            </button>
          </div>
          {error && (
            <div style={styles.errorMessage}>
              <span>❌</span>
              {error}
            </div>
          )}
          {loading ? (
            <div style={styles.loading}>
              <div
                style={{
                  width: '3rem',
                  height: '3rem',
                  border: '4px solid #e5e7eb',
                  borderTop: '4px solid #2563eb',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 1rem',
                }}
              ></div>
              <p>⏳ Loading sales returns...</p>
            </div>
          ) : salesReturns.length > 0 ? (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>SRN NO</th>
                    <th style={styles.tableHeader}>SRN DATE</th>
                    <th style={styles.tableHeader}>BILL NO</th>
                    <th style={styles.tableHeader}>BILL DATE</th>
                    <th style={styles.tableHeader}>CUSTOMER</th>
                    <th style={styles.tableHeader}>MOBILE NO</th>
                    <th style={styles.tableHeader}>NET AMOUNT</th>
                    <th style={styles.tableHeader}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {salesReturns.map((item, index) => {
                    const customerMobile = item.phone || getCustomerMobileNo(item.customer);
                    const customerDetails = getCustomerDetails(item.customer);
                    
                    return (
                      <tr
                        key={item.id}
                        style={{
                          backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <td style={styles.tableCell}>{item.srnNo}</td>
                        <td style={styles.tableCell}>{item.srnDate}</td>
                        <td style={styles.tableCell}>{item.billNo}</td>
                        <td style={styles.tableCell}>{item.billDate}</td>
                        <td style={styles.tableCell}>
                          <div>
                            <div style={{ fontWeight: '600' }}>{item.customer}</div>
                          
                          </div>
                        </td>
                        <td style={styles.tableCell}>
                          <span style={styles.mobileHighlight}>
                            {customerMobile}
                          </span>
                        </td>
                        <td style={styles.tableCell}>
                          <strong>₹{item.totals?.netAmount?.toFixed(2) || '0.00'}</strong>
                        </td>
                        <td style={styles.actionCell}>
                          <button
                            style={styles.editButton}
                            title="Edit Sales Return"
                            onClick={() => handleEdit(item)}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            style={styles.deleteButton}
                            title="Delete Sales Return"
                            onClick={() => handleDelete(item.id)}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                            }}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={styles.noData}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>📄</div>
              <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>No Sales Returns Found</h3>
              <p style={{ marginBottom: '1.5rem' }}>Get started by adding your first sales return!</p>
              <button
                onClick={() => {
                  setShowForm(true);
                  setEditData(null);
                }}
                style={{
                  ...styles.addButton,
                  fontSize: '1rem',
                  padding: '1rem 2rem',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
                }}
              >
                + Add Your First Sales Return
              </button>
            </div>
          )}
        </>
      )}
      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @media (max-width: 768px) {
          .header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }
          .addButton {
            width: 100%;
            text-align: center;
          }
        }
        button:hover:not(:disabled) {
          filter: brightness(90%);
        }
      `}</style>
    </div>
  );
};

export default SalesReturnTable;